// noinspection JSUnusedAssignment

import React from 'react';
import sanitizeHtml from 'sanitize-html';
import axiosInstance from './axios';
import themes from './themes/index';
import store, {_openModal, _setNotification, isAuthorized} from '../store';
import {extendMoment} from 'moment-range';
import debug from 'debug';
import passwordGenerator from 'password-generator';
import history from '../../history';
import DescriptiveText from '../components/DescriptiveText';
import Moment from 'moment';
import lazy from 'lazy.js';
import {PORTAL_NAME} from '../../config.json';
import qs from 'qs';

const moment = extendMoment(Moment);

const log = debug('circleblack:utils');

export const maritalStatuses = ['Single', 'Married', 'Life Partner', 'Separated', 'Divorced', 'Widowed'];

export const genders = ['Unknown', 'Male', 'Female'];

// TODO Requirements for standard display formats of text/numbers. default values? (e.g. '-')?
export const formatter = (type, options) => value => {
  const opts = options || {};
  let v;
  switch (type) {
    case 'currency':
      if (value == null) {
        return '';
      }
      v = value.toLocaleString(
        'en-US',
        {
          style: 'currency',
          currency: 'USD',
        }
      );
      if (opts.noDecimals) {
        return v.slice(v, v.length - 3);
      }
      return v;
    case 'date':
      return value && moment(value, 'YYYY-MM-DD')
        .format('M/D/YY') || '-';
    case 'datetime':
      return value && (Number.isNaN(value) ? moment(value, 'YYYY-MM-DD HH:mm:ss') : moment(value))
        .format('M/D/YY h:mma') || '-';
    case 'phone':
      return value ? value.toString()
        .replace(/[^\d]/g, '')
        .replace(/^(\d{3})(\d{3})(\d{4})\d*$/, '($1) $2-$3') : '';
    case 'taxID':
      return value && (opts.showFull ? value : `xxxx${value.substr(-4, 4)}`) || '';
    case 'name':
      return value.map(r => r || '').join(' ').replace(/ +/, ' ');
    case 'number':
      return value.toFixed(opts.decimalPlaces || 0);
    case 'percent':
      v = value.toFixed(opts.decimalPlaces || 0);
      if (Number(v) === 0 && v.charAt(0) === '-') {
        v = v.substring(1);
      }
      return v + '%';
    case 'percentDecimal':
      v = (value * 100).toFixed(opts.decimalPlaces || 0);
      if (Number(v) === 0 && v.charAt(0) === '-') {
        v = v.substring(1);
      }
      return v + '%';
    case 'object':
      return JSON.stringify(value, null, 2);
    default:
      return value;
  }
};

export const roundNumber = (num, scale = 5) => Math.round((+num + Number.EPSILON) * `1e${scale}`) / `1e${scale}`;

export const gatherData = url => (inputValue, config) => axiosInstance.get(url, {params: {...inputValue}, ...config})
  .then(res => res.data)
  .catch(function error(err) {
    log('error', err);
    throw err;
  });

export const gatherFormattedData = (url, colsToFormat, formats, opts) => inputValue => axiosInstance.get(
  url,
  {params: {...inputValue}}
)
  .then(res => {
    const data = res.data;

    for (const col of colsToFormat) {
      const format = formatter(formats[col], opts && opts[col]);

      for (const datum of data) {
        datum[col] = format(datum[col]);
      }
    }

    return data;
  })
  .catch(function error(err) {
    log('error', err);
    throw err;
  });

export const formatBytes = (bytes, decimals) => {
  const k = 1024;
  const dm = decimals || 0;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  if (!bytes && bytes !== 0) {
    return null;
  }
  if (bytes === 0) {
    return '0 Bytes';
  }
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

// ETODO come up with better name for this
export const getUpdatedFilter = filters => Object.entries(filters)
  .reduce((obj, filter) => {
    if (filter[1] != null) {
      const key = filter[0];
      const value = filter[1] && filter[1].hasOwnProperty('value') ? filter[1].value : filter[1];

      if (['isNull', 'isNotNull'].includes(value)) {
        obj[value] = obj[value] || [];
        obj[value].push(key);
        delete obj[key];
      } else {
        obj[key] = value;
      }
    }
    return obj;
  }, {});

export const calculateFixedStartDate = (minDate, asOfDate, period) => {
  log('calculateFixedStartDate', asOfDate, period);
  switch (period.value) {
    case 'MAX':
      return minDate;
    case 'YTD':
      return moment(asOfDate, 'YYYY-MM-DD')
        .startOf('year')
        .subtract(1, 'd')
        .format('YYYY-MM-DD');
    case 'QTD':
      return moment(asOfDate, 'YYYY-MM-DD')
        .startOf('quarter')
        .subtract(1, 'd')
        .format('YYYY-MM-DD');
    default:
      return moment(asOfDate, 'YYYY-MM-DD')
        .subtract(period.value.split('_')[1], period.value.split('_')[0])
        .format('YYYY-MM-DD');
  }
};

export const checkFakeEmail = email => {
  if (typeof email === 'string') {
    return new RegExp(/@cb\.[a-zA-Z0-9]+-(?:clients|advisors)\.circleblack\.com$/).test(email);
  }
  return false;
};

// ETODO reconsider needing this, copied from backend
export const generatePassword = options => {
  const opts = options || {};
  const minLength = opts.minLength || 8;
  const maxLength = opts.minLength || 20;
  const charset = opts.charset || /[!@#$%^&*?_~a-zA-Z0-9]/;
  const regex = opts.regex || /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*?_~]).{8,20}$/;
  let length = opts.length;
  let password = '1';

  if (!length) {
    length = Math.floor(Math.random() * (maxLength - minLength)) + minLength;
  }

  while (!password.match(regex)) {
    password = passwordGenerator(length, false, charset);
  }
  return password;
};

export const getRiskLabel = partner => {
  if (partner === 'riskalyze') {
    return 'Risk Number®';
  }

  if (partner === 'totum') {
    return 'Risk Score';
  }

  if (partner === 'stratifi') {
    return 'PRISM Rating';
  }

  return '';
};

export const passwordStrength = (password, messageOnly, role) => {
  const result = {};
  const notPrivilegedUsers = ['CUSTOMER', 'ADVISOR', 'ASSOC'];

  let characterLength = 12;

  if (notPrivilegedUsers.includes(role) || isAuthorized(notPrivilegedUsers)) {
    characterLength = 8;
  }

  let keyCounter = 0;

  const message = [
    <p key={`passwordStrength-${++keyCounter}`}>Password must meet the following criteria:</p>,
    <p key={`passwordStrength-${++keyCounter}`}>At least {characterLength} characters long</p>,
    <p key={`passwordStrength-${++keyCounter}`}>No more than 64 characters long</p>,
    <p key={`passwordStrength-${++keyCounter}`}>At least 1 uppercase letter</p>,
    <p key={`passwordStrength-${++keyCounter}`}>At least 1 lowercase letter</p>,
    <p key={`passwordStrength-${++keyCounter}`}>At least 1 number</p>,
    <p key={`passwordStrength-${++keyCounter}`}>At least 1 symbol/special character</p>,
    <p key={`passwordStrength-${++keyCounter}`}>Cannot use more than 2 characters consecutively</p>,
    <p key={`passwordStrength-${++keyCounter}`}>NOTE: Cannot reuse a previous password (last 12 times)</p>,
  ];

  if (messageOnly) {
    return {message};
  }

  result.minLength = (password.length >= characterLength);
  result.maxLength = (password.length <= 64);
  result.upperCase = !!password.match(/[A-Z]/);
  result.lowerCase = !!password.match(/[a-z]/);
  result.number = !!password.match(/\d/);
  result.symbol = !!password.match(/[!@#$%^&*?_~\-=\(\)`\[\]\;\',.\/{}|:\"<>]/);
  result.consecutiveCharacters = !password.match(/(.)\1\1/i);

  return {
    valid: result.minLength
      && result.maxLength
      && result.upperCase
      && result.lowerCase
      && result.number
      && result.symbol
      && result.consecutiveCharacters,
    message,
  };
};

export const open = (verb, url, data, target) => {
  const form = document.createElement('form');
  form.action = url;
  form.method = verb;
  form.target = target || '_self';
  if (data) {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const input = document.createElement('textarea');
        input.name = key;
        input.value = typeof data[key] === 'object' ? JSON.stringify(data[key]) : data[key];
        form.appendChild(input);
      }
    }
  }
  form.style.display = 'none';
  document.body.appendChild(form);
  form.submit();
};
/**
 * Sets uri for corresponding section location if it was from Reports or Documents
 * @param {string} [uri] - portion of the uri that would stay consistent
 * @returns {string}
 */
export const setSectionURI = (uri = '') => {
  const uriSection = history.location.pathname.split('/', 3).find(el => ['report', 'documents', 'content'].includes(el));
  let targetURI = '/';
  if (uriSection === 'report') {
    targetURI = `/report${uri}`;
  } else if (uriSection === 'documents') {
    targetURI = PORTAL_NAME === 'investor' ? `/documents${uri}` : `/engage/documents${uri}`;
  } else if (uriSection === 'content') {
    targetURI = `/compliance/content${uri}`;
  }
  return targetURI;
};

export const openDocument = (doc, dataManager, compliance, hideDocumentDetails, attachment) => {
  if ((doc.fileFormat !== 'html' && doc.fileFormat !== 'pdf') || attachment) {
    const queryParams = {
      ...(compliance && {compliance: 1}),
      ...(attachment && {attachment: 1}),
    };
    return axiosInstance.get(`/documents/${doc.id}/download`, {params: queryParams})
      .then(function getDocumentResolve(res) {
        // eslint-disable-next-line no-unused-vars
        let winOpen = window.open(res.data.uri, '_blank');
        // eslint-disable-next-line no-unused-vars
        winOpen = null; // "window.open()" vulnerability fix
      })
      .catch(function handleError(err) {
        const res = err.response;
        if (!(res.status < 500 && res.data.error && res.data.error.msg)) {
          err.message = 'Unfortunately we encountered an error retrieving the document. If you have any questions, please contact CircleBlack Client Services at help@circleblack.com or 800-315-1241.';
        }
        store.dispatch(_openModal(
          {
            type: 'Error',
            title: 'Failed to Retrieve Document',
            err: err,
          }
        ));
      });
  }
  store.dispatch(_openModal({
    componentName: 'ViewDocument',
    document: doc,
    dataManager: dataManager,
    compliance: compliance,
    hideDocumentDetails: hideDocumentDetails,
  }));
};

export const getShareText = (document, compliance, profileId) => {
  const isInvestorOwner = PORTAL_NAME === 'investor' && document.ownerID !== profileId;

  let disableShareDetails = false;
  let shareText;

  if (document && document.shareApproval === 'DENIED') {
    disableShareDetails = true;

    shareText = (<div><p>You attempted to share this document but it was denied by compliance{document.notes
      ? ':'
      : '.'}</p><p>{document.notes ? '"' + document.notes + '"' : null}</p></div>);
  } else if (document && (document.notShareable || isInvestorOwner)) {
    disableShareDetails = true;

    shareText = document && `${document.title} was shared with you by ${document.author} on ${moment(document.distributionDate, 'YYYY-MM-DD')
      .format('MM/DD/YYYY')}. You cannot currently share this with other users.`;
  } else {
    switch (document && document.cat) {
      case 'Report':

        if (!document.assocHousehold) {
          disableShareDetails = true;
          shareText = 'Reports can only be shared with clients if the report is associated with one household.';
        } else {
          shareText = `${document.title} is intended for ${document.assocHousehold} and can only be shared with them.`;
        }
        break;
      default:
        shareText = `${compliance ? '' : 'You can click the "Share Details" button to send '
          + document.title
          + ' to selected or targeted households. '} The "Share Details" page will show you who the document has already been shared with and who has viewed it. You can also select to "Unshare" the document from select households or from all.`;
    }
  }

  return {disableShareDetails, shareText};
};

export const dataFeedStatuses = PORTAL_NAME !== 'widgets' && {
  'Data Current': {
    status: 'Data Current',
    description: 'The data being displayed is current as of the last business day.',
    icon: 'dataCurrent',
    // classes: 'data-current',
    color: '#99FA66',
  },
  'Delay of Feed': {
    status: 'Delay of Feed',
    description: 'The data being displayed is as of the previous business day since the vendor feed is delayed.',
    icon: 'delayOfFeed',
    // classes: 'delay-of-feed',
    color: '#9F3D93',
  },
  'Not Aggregating': {
    status: 'Not Aggregating',
    description: 'The held-away account needs the client’s attention.',
    icon: 'notAggregating',
    // classes: 'not-aggregating',
    color: '#FFCC03',
  },
  'Not Applicable': {
    status: 'Not Applicable',
    description: 'There is no data feed status for this account.',
    icon: 'notApplicable',
    // classes: 'not-applicable',
    color: '#797B83',
  },
} || {};

export const currentLocation = () => window.location.pathname + window.location.search + window.location.hash;

/**
 * Gathers the current url including query params and hash, returns a function that when executed navigates there.
 * @return {function(): *}
 */
export const backAction = () => {
  const currentState = currentLocation();
  return () => history.push(currentState);
};

/**
 * Given an error from an api call, get the message
 * @param err
 * @return {any}
 */
export const parseErrorMessage = err => {
  const brand = store.getState()?.product?.brand;
  return (err.response && err.response.data && err.response.data.msg)
    || (err.response && err.response.data && err.response.data.error && err.response.data.error.msg)
    || err.message || err.msg
    || `We're sorry, there was an issue with the current page. Please try refreshing the page. If the issue persists please contact us at ${brand.clientServicesEmail} or ${brand.clientServicesPhone}.`;
};

export const regex = {
  email: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i,
  emails: /(?:[,|; ]{1,2}|^)((([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,})))/ig,
  password: /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/,
};

export const isValidHouseholdEmail = email => !(!email || checkFakeEmail(email) || !email.match(regex.email));

/**
 * Navigates user to their 'home' page
 * @param role
 * @param logoAction
 * @return {*} Navigates using history
 */
export const goHome = (role, logoAction) => {
  const getInvestorHomePage = () => {
    const hideWelcome = !!store.getState().session?.profile?.hideWelcome;
    const homePage = '/portfolio';
    const welcomePage = '/welcome';

    if (logoAction) {
      return '/portfolio';
    }
    return hideWelcome ? homePage : welcomePage;
  };

  const historyState = {};
  switch (role) {
    case 'PRODMGR':
    case 'PRODSUPP':
      return history.push('/product/firms');
    case 'ASSOC':
      return history.push('/assist');
    case 'CPLO':
    case 'ADVISOR':
    case 'BRANCHMGR':
    case 'FIRMMGR':
    default:
      if (window.location.pathname.startsWith('/analyze/dashboard')) {
        historyState.force = true;
      }
      if (PORTAL_NAME === 'ops') {
        return history.push('/', historyState);
      }
      if (PORTAL_NAME === 'investor') {
        return history.push(getInvestorHomePage());
      }
      return history.push('/analyze/dashboard', historyState);
  }
};

export const displayedCriteriaLookup = {
  accountType: {
    inputType: 'dropdown',
    displayName: 'Account Type',
    StepTwoDisplayName: 'Account Type',
  },
  actualRisk: {
    inputType: 'number',
    displayName: 'Actual Risk',
    format: 'number',
    decimalPlaces: 0,
  },
  advisorID: {
    inputType: 'dropdown',
    displayName: 'Advisor',
    StepTwoDisplayName: 'Advisor Name',
  },
  age: {
    inputType: 'number',
    displayName: 'Age',
    format: 'number',
    decimalPlaces: 0,
  },
  assetClass: {
    inputType: 'assetClass',
    displayName: 'Exposure',
    StepTwoDisplayName: 'Exposure Type',
    format: 'percent',
    decimalPlaces: 2,
  },
  assetClassValue: {
    inputType: 'assetClassValue',
  },
  balance: {
    inputType: 'number',
    displayName: 'Portfolio Value',
    format: 'currency',
    decimalPlaces: 2,
  },
  bondSector: {
    inputType: 'dropdown',
    displayName: 'Bond in a Sector',
    StepTwoDisplayName: 'Bond Sector',
  },
  country: {
    inputType: 'dropdown',
    displayName: 'Stock in a Country',
    StepTwoDisplayName: 'Country',
  },
  fundCategory: {
    inputType: 'dropdown',
    displayName: 'Fund in a Category',
    StepTwoDisplayName: 'Fund Category',
  },
  insightDate: {
    inputType: 'date',
    displayName: 'Latest Insight Date',
  },
  region: {
    inputType: 'dropdown',
    displayName: 'Stock in a Region',
    StepTwoDisplayName: 'Region',
  },
  securityID: {
    inputType: 'dropdown',
    displayName: 'Individual Security',
    StepTwoDisplayName: 'Security Name',
  },
  stockSector: {
    inputType: 'dropdown',
    displayName: 'Stock in a Sector',
    StepTwoDisplayName: 'Stock Sector',
  },
  successProbability: {
    inputType: 'number',
    displayName: 'Probability of Success',
    format: 'percent',
    decimalPlaces: 1,
  },
  targetRisk: {
    inputType: 'number',
    displayName: 'Target Risk',
    format: 'number',
    decimalPlaces: 0,
  },
};

export const formatSelectedCriteria = (input) => {
  const criteria = {};
  for (const property in input) {
    if (input.hasOwnProperty(property)) {
      if (input[property].value) {
        criteria[input[property].id] = input[property].value;
      }
      if (input[property].date) {
        criteria[input[property].id] = input[property].date;
      }
      let str = '';
      const percentModifier = displayedCriteriaLookup[input[property].id]?.format === 'percent' ? 100 : 1;
      if (input[property].greaterThan && input[property].lessThan) {
        str = Math.round(input[property].greaterThan * percentModifier * 100) / 100
          + '<>'
          + Math.round(input[property].lessThan * percentModifier * 100) / 100;
      } else {
        if (input[property].greaterThan) {
          str = '>' + Math.round(input[property].greaterThan * percentModifier * 100) / 100;
        }
        if (input[property].lessThan) {
          str = '<' + Math.round(input[property].lessThan * percentModifier * 100) / 100;
        }
      }
      if (!!str) {
        if (input[property].id === 'assetClass') {
          criteria.assetClassValue = str;
        } else {
          criteria[input[property].id] = str;
        }
      }
    }
  }
  return criteria;
};

export const parseGreaterThanLessThan = (str) => {
  const result = {
    greaterThan: 0,
    lessThan: 0,
  };
  if (str[0] === '>') {
    result.greaterThan = str.substring(1, str.length);
  } else if (str[0] === '<') {
    result.lessThan = str.substring(1, str.length);
  } else {
    const greaterThanLessThanIdx = str.indexOf('<>');
    result.greaterThan = str.substring(0, greaterThanLessThanIdx);
    result.lessThan = str.substring(greaterThanLessThanIdx + 2, str.length);
  }
  return result;
};

export const formatCriteriaState = (criteria) => {
  let formattedCriteria = {};
  let securityID;
  let secIdx;
  let advisorID;
  let advIdx;
  if (typeof criteria === 'object') {
    Object.keys(criteria).map((criteriaType, idx) => {
      if (criteriaType === 'securityID') {
        securityID = criteria[criteriaType];
        secIdx = idx;
      }
      if (criteriaType === 'advisorID') {
        advisorID = criteria[criteriaType];
        advIdx = idx;
      }
      const percentModifier = displayedCriteriaLookup[criteriaType].format === 'percent' ? 100 : 1;
      if (displayedCriteriaLookup[criteriaType].inputType === 'number') {
        const greaterThanLessThan = parseGreaterThanLessThan(criteria[criteriaType]);
        formattedCriteria[idx] = {
          id: criteriaType,
          label: displayedCriteriaLookup[criteriaType].displayName,
          greaterThan: Math.round(greaterThanLessThan.greaterThan / percentModifier * 100) / 100,
          lessThan: Math.round(greaterThanLessThan.lessThan / percentModifier * 100) / 100,
        };
      } else if (displayedCriteriaLookup[criteriaType].inputType === 'date') {
        formattedCriteria[idx] = {
          id: criteriaType,
          label: displayedCriteriaLookup[criteriaType].displayName,
          date: criteria[criteriaType],
        };
      } else if (displayedCriteriaLookup[criteriaType].inputType === 'assetClass') {
        const greaterThanLessThan = parseGreaterThanLessThan(criteria.assetClassValue);
        formattedCriteria[idx] = {
          id: criteriaType,
          label: displayedCriteriaLookup[criteriaType].displayName,
          value: criteria[criteriaType],
          greaterThan: Math.round(greaterThanLessThan.greaterThan / percentModifier * 100) / 100,
          lessThan: Math.round(greaterThanLessThan.lessThan / percentModifier * 100) / 100,
        };
      } else if (displayedCriteriaLookup[criteriaType].inputType !== 'assetClassValue') {
        formattedCriteria[idx] = {
          id: criteriaType,
          label: displayedCriteriaLookup[criteriaType].displayName,
          value: criteria[criteriaType],
        };
      }
    });
  }
  if (typeof criteria === 'string') {
    formattedCriteria = criteria;
  }
  return Promise.resolve(!!securityID ? axiosInstance.get(`securities/${securityID}`) : null)
    .then((sec) => {
      if (sec) {
        formattedCriteria[secIdx].securityName = sec.data.name;
      }
      if (!!advisorID) {
        return axiosInstance.get(`advisors/${advisorID}`)
          .then((adv) => {
            if (adv) {
              formattedCriteria[advIdx].value = adv.data.householdName;
            }
          });
      }
    })
    .then(() => formattedCriteria);
};

export const getApprovals = (doc, setApprovals, setApprovedOrDenied) => {
  let approvedOrDenied = {
    val: 'ApprovedWithCriteria',
  };
  axiosInstance.get(`/documents/${doc.documentID || doc.id}/approvals?compliance=1`)
    .then((res) => Promise.all(res.data.map((approval) => {
      if (typeof approval.criteria === 'string') {
        approvedOrDenied = {
          val: approval.criteria,
          notes: approval.notes,
        };
      }
      return formatCriteriaState(approval.criteria)
        .then((criteria) => (
          {
            ...approval,
            criteria: criteria,
          }
        ));
    })))
    .then((result) => {
      setApprovals(result);
      setApprovedOrDenied(approvedOrDenied);
      return result;
    });
};

export const getStepTwoCriteria = arr => Promise.all(arr.map(name => {
  switch (name) {
    case 'accountType':
      return axiosInstance.get('accounts/types').then(res => res.data);
    case 'assetClass':
      return axiosInstance.get('exposures/assetClasses').then(res => res.data);
    case 'bondSector':
      return axiosInstance.get('exposures/bondSectors').then(res => res.data);
    case 'country':
      return axiosInstance.get('countries?sort=+countryName').then(res => res.data);
    case 'fundCategory':
      return axiosInstance.get('securities/fundCategories').then(res => res.data);
    case 'region':
      return axiosInstance.get('exposures/regions').then(res => res.data);
    case 'stockSector':
      return axiosInstance.get('exposures/stockSectors').then(res => res.data);
    case 'securityID':
      return axiosInstance.get('securities').then(res => res.data);
    default:
      return Promise.resolve(null);
  }
}));

export const initialStepTwoCriteriaOptions = {
  accountType: {
    options: [],
    field: 'name',
  },
  actualRisk: {
    format: 'number',
    decimalPlaces: 0,
  },
  age: {
    format: 'number',
    decimalPlaces: 0,
  },
  assetClass: {
    options: [],
    format: 'percent',
    decimalPlaces: 2,
    placeholder: '%',
    field: 'typeName',
  },
  assetClassValue: {},
  balance: {
    format: 'currency',
    decimalPlaces: 2,
    placeholder: '$0.00',
  },
  bondSector: {
    options: [],
    field: 'typeName',
  },
  country: {
    options: [],
    field: 'countryName',
  },
  fundCategory: {
    options: [],
    field: 'name',
  },
  insightDate: {},
  region: {
    options: [],
    field: 'name',
  },
  securityID: {
    options: [],
  },
  stockSector: {
    options: [],
    field: 'typeName',
  },
  successProbability: {
    format: 'percent',
    decimalPlaces: 1,
    placeholder: '%',
  },
  targetRisk: {
    format: 'number',
    decimalPlaces: 0,
  },
};

export const updateStepTwoCriteriaOptions = (dataArr, nameArr) => {
  const newStepTwoCriteria = initialStepTwoCriteriaOptions;
  for (let i = 0; i < nameArr.length; i++) {
    let field = '';
    let newArr = [];
    switch (nameArr[i]) {
      case 'accountType':
      case 'assetClass':
      case 'bondSector':
      case 'country':
      case 'fundCategory':
      case 'region':
      case 'stockSector':
        field = initialStepTwoCriteriaOptions[nameArr[i]].field;
        newArr = lazy(dataArr[i]).pluck(field).uniq().toArray();
        newArr.forEach(function modifyArray(elem, index) {
          this[index] = {label: elem, value: elem};
        }, newArr);
        newStepTwoCriteria[nameArr[i]] = {
          ...initialStepTwoCriteriaOptions[nameArr[i]],
          options: newArr,
        };
        break;
      case 'securityID':
        newArr = lazy(dataArr[i])
          .map((elem) => ({
            label: elem.name,
            value: elem.id,
          }))
          .uniq()
          .toArray();
        newStepTwoCriteria.securityID = {
          ...initialStepTwoCriteriaOptions.securityID,
          options: newArr,
        };
        break;
      default:
    }
  }
  return newStepTwoCriteria;
};

export const setReportParameters = (reportParameters, requiredParams) => reportParameters
  .filter(param => !!Object.keys(requiredParams).includes(param.name))
  .map(parameter => ({id: parameter.id, value: requiredParams[parameter.name]}));

export const requestReport = (reportName, params) => axiosInstance(`/reports/${reportName}`)
  .then(res => {
    const report = res.data;
    const postParameters = setReportParameters(report.parameters, params);
    return axiosInstance.post(`/reports/${reportName}`, {parameters: postParameters})
      .then(response => {
        if (response.status >= 200 && res.status < 400) {
          store.dispatch(_setNotification({message: 'The report you requested is generating. You may view it shortly.'}));
        }
      });
  })
  .catch(err => {
    // TODO KST-1840 Can i have this here for all or could this break outside of advisor overview?
    if (err.status === 401 && err.data.error && err.data.error === 401) {
      store.dispatch(_setNotification({message: 'You must set a report password before generating this report.'}));
    }
    store.dispatch(_setNotification({message: 'There was a problem generating the report you requested.'}));
  });

export const exportOverview = (context, exportType, containerStartDate) => {
  let reportName;
  let data = {};
  let modalType = 'ConfirmModal';
  const {containerID} = context;

  if (isAuthorized(['CPLO'])) return null;

  if (exportType === 'householdList') {
    reportName = 'Advisor Households';
    data = {containerID};
  } else if (exportType === 'accountList') {
    reportName = 'Advisor Accounts';
    data = {containerID};
  } else if (context.view && ['FIRM', 'BRANCH', 'ADVISOR'].includes(context.view.type)) {
    const advisorID = context?.view?.type === 'ADVISOR' ? context.view.id : null;
    modalType = 'PeriodSelectionModal';
    switch (context.type) {
      case 'ACCOUNT':
        reportName = 'Advisor Account Overview';
        data = advisorID ? {containerID, advisorID} : {containerID};
        break;
      case 'ADVISOR_GROUP':
        reportName = 'Advisor Group Overview';
        data = advisorID ? {containerID, advisorID} : {containerID};
        break;
      case 'HOUSEHOLD':
        reportName = 'Advisor Household Overview';
        data = advisorID ? {containerID, advisorID} : {containerID};
        break;
      default:
        return;
    }
  } else {
    switch (context.type) {
      case 'FIRM':
      case 'BRANCH':
      case 'ADVISOR':
        modalType = 'PeriodSelectionModal';
        reportName = 'Advisor Overview';
        data = {containerID};
        break;
      case 'ADVISOR_GROUP':
        modalType = 'PeriodSelectionModal';
        reportName = 'Advisor Group Overview';
        data = {containerID};
        break;
      case 'ACCOUNT':
        reportName = 'Advisor Accounts';
        data = {containerID};
        break;
      case 'HOUSEHOLD':
        reportName = 'Advisor Households';
        data = {containerID};
        break;
      default:
        return;
    }
  }

  return store.dispatch(_openModal({
    type: modalType,
    title: 'Export Page',
    body: <DescriptiveText>The data from the current page will be exported as a report.</DescriptiveText>,
    data: {containerStartDate, containerID},
    button: {
      action: (parameters) => requestReport(reportName, parameters),
      text: 'Export',
      data,
    },
  }));
};

export const getDatesBetween = (start, end) => {
  const range = moment.range(start, end);
  return Array.from(range.by('days')).map(function format(m) {
    return m.format('YYYY-MM-DD');
  });
};

export const formatBranding = brand => {
  // UPDATE FOR OTHER UIs!
  const portal = ['advisor', 'ops'].includes(PORTAL_NAME) ? 'advisor' : PORTAL_NAME;
  const formattedBrand = {...brand};
  const properties = [
    'BackgroundImage',
    'CSEmail',
    'CSPhone',
    'GeneralDisclosures',
    'MarketingMsg',
    'Media',
    'MediaType',
    'PlatformName',
    'Icon',
  ];
  properties.map(property => {
    const newPropertyName = ['CSEmail', 'CSPhone'].includes(property)
      ? `clientServices${property.slice(2)}`
      : `${property[0].toLowerCase()}${property.slice(1)}`;
    formattedBrand[newPropertyName] = formattedBrand[`${portal}${property}`];
    delete formattedBrand[`advisor${property}`];
    delete formattedBrand[`investor${property}`];
  });
  return formattedBrand;
};

export const sanitizeInjection = (text) => {
  const htmlText = sanitizeHtml(text || '');
  const isHtml = !!htmlText.match(/<([^>]+)>/);

  if (isHtml) {
    return htmlText;
  }

  return htmlText
    .replace(/\n|\r\n|\n\r|\r/g, '<br>')
    .replace(/\\n|\\r\\n|\\n\\r|\\r/g, '<br>');
};

export const getPortalTheme = () => themes[PORTAL_NAME];

export const buildQueryContext = (context, additional = {}) => qs.stringify({
  context: context && btoa(JSON.stringify(context)) || undefined,
  ...additional,
});

/**
 * Gathers value from preferences object given collection, group and core
 * @param pref Preferences object
 * @param collection Collection title
 * @param group Group title
 * @param core Core title
 * @return {*} Preferences value
 */
export const getPreferencesValue = (pref, collection, group, core) => {
  const prefCollection = (pref && pref.collections || []).find(v => v.title === collection);
  const prefGroup = (prefCollection && prefCollection.groups || []).find(v => v.title === group);
  const prefCore = (prefGroup && prefGroup.cores || []).find(v => v.title === core);
  return prefCore && prefCore.val;
};

/**
 * Calculates general aggregation status based on individual institutions statuses
 * 1. If there is no aggregation issues, returns 'SUCCESS'
 * 2. If there are any authentication issues, returns 'AUTH_ISSUES'
 * 3. If there are only non-authentication issues, returns 'AGGREGATION_ISSUES'
 * @param institutions Institutions array
 * @return {'SUCCESS'|'AUTH_ISSUES'|'AGGREGATION_ISSUES'} Aggregation status
 */
export const calculateAggregationStatus = (institutions = []) => {
  const hasAuthIssues = !!institutions.find(v => ['AUTH_FAILED'].includes(v.status));
  const hasAggregationIssues = !!institutions.find(v => ['YODLEE_ERROR'].includes(v.status));

  if (!hasAuthIssues && !hasAggregationIssues) {
    return 'SUCCESS';
  }
  if (hasAuthIssues) {
    return 'AUTH_ISSUES';
  }
  return 'AGGREGATION_ISSUES';
};

export const checkV3ReleaseModals = profile => {
  if (profile && PORTAL_NAME === 'advisor') {
    const modals = [
      'v3PreReleaseModal',
      'v3PostReleaseModal',
    ];
    const toOpen = modals.find(modal => profile[modal]);
    if (toOpen) {
      store.dispatch(_openModal({
        componentName: toOpen[0].toUpperCase() + toOpen.slice(1),
      }));
    }
  }
};
